import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { fehlertext } from '../../lib/fehlertext';
import { useAuth } from '../../contexts/AuthContext';
import { codeGueltig } from '../../lib/zweiFaktor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { CodeFelder } from './CodeFelder';
import { GeraetMerken } from './GeraetMerken';
import { merkerFuer, merkerSpeichern } from '../../lib/geraet';
import { Assistent } from './Assistent';

const ABGELAUFEN = 'Die Anmeldung ist abgelaufen. Bitte melden Sie sich erneut an.';
const FALSCH = 'Der Code ist nicht richtig. Bitte versuchen Sie es erneut.';

const LinkKnopf = ({ onClick, children, testid }) => (
  <button type="button" onClick={onClick} className="text-sm text-blue-700 hover:text-blue-900 hover:underline" data-testid={testid}>{children}</button>
);

// 2FA-Schritt der Anmeldung (in derselben Karte wie das Formular).
export const ZweiFaktorSchritt = ({ art, zwischenToken, onZurueck, email }) => {
  const [merken, setMerken] = useState(true);
  const navigate = useNavigate();
  const { anmeldungAbschliessen, kontoNeuLaden } = useAuth();
  const einrichtung = art === 'einrichtung_erforderlich';
  const [code, setCode] = useState('');
  const [wiederherstellung, setWiederherstellung] = useState(false);
  const [ersatz, setErsatz] = useState('');
  const [pruefe, setPruefe] = useState(false);
  const [fehler, setFehler] = useState('');
  const feldRef = useRef(null);

  const abschliessen = (antwort) => {
    anmeldungAbschliessen(antwort);
    kontoNeuLaden();
    toast.success('Erfolgreich angemeldet!');
    navigate('/');
  };

  const abgelaufen = () => onZurueck(ABGELAUFEN);

  const pruefen = async (wert) => {
    if (!codeGueltig(wert) || pruefe) return;
    setPruefe(true);
    setFehler('');
    try {
      const res = await api.post('/auth/2fa/pruefen', {
        zwischen_token: zwischenToken, code: wert.trim(), geraet_merker: merkerFuer(email), geraet_merken: merken,
      });
      merkerSpeichern(email, res.data?.geraet_merker);
      abschliessen(res.data);
    } catch (err) {
      if (err?.response?.status === 401) { abgelaufen(); return; }
      setFehler(fehlertext(err, FALSCH));
      setCode('');
      setErsatz('');
      setTimeout(() => feldRef.current && feldRef.current.focus(), 0);
    } finally {
      setPruefe(false);
    }
  };

  const knopfInhalt = pruefe ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird geprüft...</> : 'Anmelden';

  return (
    <>
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-2xl font-bold text-center font-[Manrope]" data-testid="zwei-faktor-titel">
          {einrichtung ? 'Zwei-Faktor-Anmeldung einrichten' : 'Zwei-Faktor-Anmeldung'}
        </CardTitle>
        <CardDescription className="text-center">
          {einrichtung
            ? 'Zum Schutz der Daten ist die Zwei-Faktor-Anmeldung jetzt Pflicht. Das dauert 2 Minuten.'
            : 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4" data-testid="zwei-faktor-schritt">
        {fehler && (
          <Alert variant="destructive" className="py-2" data-testid="zwei-faktor-login-fehler">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{fehler}</AlertDescription>
          </Alert>
        )}
        {einrichtung ? (
          <Assistent zwischenToken={zwischenToken} email={email} onFertig={abschliessen} onAbbrechen={(err) => (err ? abgelaufen() : onZurueck())} />
        ) : wiederherstellung ? (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); pruefen(ersatz); }}>
            <GeraetMerken merken={merken} onChange={setMerken} />
            <div className="space-y-2">
              <Label htmlFor="wiederherstellungscode">Wiederherstellungscode</Label>
              <Input id="wiederherstellungscode" value={ersatz} onChange={(e) => setErsatz(e.target.value)} placeholder="xxxxx-xxxxx" autoComplete="off" autoFocus className="h-11" ref={feldRef} data-testid="zwei-faktor-wiederherstellung-feld" />
            </div>
            <Button type="submit" className="w-full h-11 bg-blue-800 hover:bg-blue-900" disabled={pruefe || !codeGueltig(ersatz)} data-testid="zwei-faktor-wiederherstellung-anmelden">{knopfInhalt}</Button>
            <div className="text-center"><LinkKnopf onClick={() => { setWiederherstellung(false); setFehler(''); }} testid="zwei-faktor-app-code-verwenden">Code aus der App verwenden</LinkKnopf></div>
          </form>
        ) : (
          <div className="space-y-3">
            <GeraetMerken merken={merken} onChange={setMerken} />
            <CodeFelder wert={code} onChange={setCode} onComplete={pruefen} feldRef={feldRef} disabled={pruefe} />
            <Button type="button" className="w-full h-11 bg-blue-800 hover:bg-blue-900" disabled={pruefe || !codeGueltig(code)} onClick={() => pruefen(code)} data-testid="zwei-faktor-anmelden">{knopfInhalt}</Button>
            <div className="text-center"><LinkKnopf onClick={() => { setWiederherstellung(true); setFehler(''); }} testid="zwei-faktor-wiederherstellung-verwenden">Wiederherstellungscode verwenden</LinkKnopf></div>
          </div>
        )}
        {/* Bei der Einrichtung führt nur „Abbrechen“ zurück – in Schritt 4 gar nichts (Codes gingen verloren). */}
        {!einrichtung && (
          <div className="border-t pt-3 text-center">
            <LinkKnopf onClick={() => onZurueck()} testid="zwei-faktor-zurueck-zur-anmeldung">Zurück zur Anmeldung</LinkKnopf>
          </div>
        )}
      </CardContent>
    </>
  );
};

export default ZweiFaktorSchritt;
